import { getErrorsFromAIMessageContent } from "@/components/Canvas/hooks/useStoryboardErrors";
import { useSelectedElements } from "@/hooks/useSelectedElements";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { Message, MessageContext } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import { UploadedFile } from "@/utils/types";
import { fixJson, useCanvasId, useUserId } from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import { EngSpecFileDiff } from "../../EngSpec/EngSpec";
import { VALID_COMMANDS } from "../useApplyChatBlock";

export interface MessageSegment {
  type:
    | "text"
    | "codeSnippet"
    | "other-block"
    | "aiCodeChange"
    | "aiChanges"
    | "action-progress"
    | "dev-errors"
    | "prd-json"
    | "figmaInput"
    | "reasoning"
    | "reasoning-timing"
    | "code-edit-stream"
    | "search-grounding-sources"
    | "vercel-deployment-message"
    | "env-variables-input";

  // make new type
  makeNew?: any;

  // text type
  content?: string;

  // other types
  commit?: any;
  file?: any;
  plan?: any;
  codeSnippet?: any;
  diffCodeSnippet?: any;

  aiCodeChange?: any;
  aiChanges?: any;

  // Parsed json object (OLD: XML-based reasoning)
  parsedJson?: any;
  jsonIsBroken?: boolean;

  // True when streaming, but should be false when done streaming
  jsonIncomplete?: boolean;

  reasoning?: {
    content: string;
    thinkingTimeSeconds?: number;
  };

  toolCall?: boolean;
  toolCallStatus?: "in-progress" | "succeeded" | "failed" | "aborted";
  toolCallId?: string;
  toolCallName?: string;
  toolCallResult?: any;
  refreshAfterResponse?: boolean;
}

export const parseMessageContentBlocks = (
  message: Message,
  externalToolResults?: { [toolCallId: string]: any },
): MessageSegment[] => {
  const segments: MessageSegment[] = [];

  if (!message) return segments;

  if (message.role === "user" && message.message_context?.codeSnippet) {
    segments.push({
      type: "codeSnippet",
      codeSnippet: message.message_context?.codeSnippet,
      diffCodeSnippet: message.message_context?.diffCodeSnippet,
    });
  }

  // Normalize all message content to combined format for single processing path
  const normalizeToMaxFormat = (message: Message): any[] => {
    if (
      typeof message.content === "string" &&
      message.content?.startsWith("[")
    ) {
      try {
        return JSON.parse(message.content);
      } catch (e) {
        // If JSON parsing fails, treat as old format text
        return [{ type: "text", text: message.content }];
      }
    } else if (Array.isArray(message.content)) {
      return message.content;
    } else {
      // Handle Strings
      const result: any[] = [];

      // Add text content if present
      if (message.content) {
        result.push({ type: "text", text: message.content });
      }

      return result;
    }
  };

  const processTextContent = (textContent: string) => {
    const lines = textContent.split("\n");
    const textBlocks: string[] = [];
    let currentBlock = "";
    let insideExampleBlock = false;

    lines.forEach((line) => {
      if (line.startsWith("```")) {
        currentBlock += line + "\n";
        insideExampleBlock = !insideExampleBlock;
      } else if (line.startsWith("</") && !insideExampleBlock) {
        currentBlock += line;
        textBlocks.push(currentBlock);
        currentBlock = "";
      } else if (line.startsWith("<") && !insideExampleBlock) {
        textBlocks.push(currentBlock);
        currentBlock = line + "\n";
      } else {
        currentBlock += line + "\n";
      }
    });

    if (currentBlock.trim()) {
      textBlocks.push(currentBlock.trim());
    }

    // Process each text block
    textBlocks.forEach((content) => {
      if (!content.startsWith("<")) {
        if (content.trim()) {
          segments.push({
            type: "text",
            content: content.trim(),
          });
        }
      } else {
        // Handle XML blocks (reasoning, ai-changes, etc.)
        const contentLines = content.split("\n");
        const blockClosed =
          contentLines[contentLines.length - 1].startsWith("</");
        const blockType = contentLines[0].replace("<", "").replace(">", "");

        if (blockType === "figma_input") {
          segments.push({
            type: "figmaInput",
            content: contentLines
              .slice(1, blockClosed ? -1 : undefined)
              .join("\n")
              .replace("<", "")
              .replace(">", ""),
          });
        } else if (blockType === "vercel_deployment_message") {
          let parsedJson = {};
          try {
            parsedJson = JSON.parse(
              contentLines
                .slice(1, blockClosed ? -1 : undefined)
                .join("\n")
                .trim(),
            );
          } catch (e) {}
          segments.push({
            type: "vercel-deployment-message",
            parsedJson,
          });
        } else if (blockType === "dev-errors") {
          segments.push({
            type: blockType,
            parsedJson: getErrorsFromAIMessageContent(content),
            jsonIsBroken: false,
          });
        } else if (blockType === "code-edit-stream") {
          segments.push({
            type: "code-edit-stream",
            content: contentLines
              .slice(1, blockClosed ? -1 : undefined)
              .join("\n")
              .trim(),
          });
        } else if (
          blockType === "code-change-json" ||
          blockType === "ai-changes" ||
          blockType === "action-progress" ||
          blockType === "prd-json" ||
          blockType === "reasoning" ||
          blockType === "reasoning-timing" ||
          blockType === "search-grounding-sources" ||
          blockType === "env-variables-input"
        ) {
          try {
            const leftover = contentLines
              .slice(1, blockClosed ? -1 : undefined)
              .join("\n")
              .trim();

            const fixJsonRes = fixJson(leftover);
            const parsedObj = fixJsonRes.fixedObj;
            const jsonIsBroken = fixJsonRes.broken;
            let segmentBlock: MessageSegment;

            if (blockType === "code-change-json") {
              segmentBlock = {
                type: "aiCodeChange",
                content,
                jsonIsBroken,
                jsonIncomplete: !fixJsonRes.complete,
                aiCodeChange: parsedObj,
              };
            } else if (blockType === "ai-changes") {
              segmentBlock = {
                type: "aiChanges",
                content,
                jsonIsBroken,
                jsonIncomplete: !fixJsonRes.complete,
                aiChanges: parsedObj,
              };
              // Add an id and an executedActivityId to each change
              parsedObj.changes = parsedObj?.changes?.map?.((change, idx) => {
                return {
                  id: `change-from-chat-${message.id}-${idx}`,
                  executedActivityId: message.message_context?.savedActivityId,
                  ...change,
                };
              });
            } else {
              segmentBlock = {
                type: blockType as MessageSegment["type"],
                content,
                jsonIsBroken,
                jsonIncomplete: !fixJsonRes.complete,
                parsedJson: parsedObj,
              };
            }
            segments.push(segmentBlock);
          } catch (e) {}
        } else {
          segments.push({
            type: "other-block",
            content:
              "```" +
              contentLines.slice(1, blockClosed ? -1 : undefined).join("\n") +
              "```",
          });
        }
      }
    });
  };

  const processToolCall = (
    toolCallBlock: any,
    statusMap: { [toolCallId: string]: "succeeded" | "failed" } = {},
    toolResultsMap: { [toolCallId: string]: any } = {},
  ) => {
    const toolName = toolCallBlock.toolName;
    const toolCallId = toolCallBlock.toolCallId;
    // Use status from the block first, then from statusMap, then default to in-progress
    const status =
      toolCallBlock.status || statusMap[toolCallId] || "in-progress";

    segments.push({
      type: "text",
      content: JSON.stringify(toolCallBlock.args || {}),
      toolCall: true,
      toolCallStatus: status,
      toolCallId: toolCallId,
      toolCallName: toolName,
      toolCallResult: toolResultsMap[toolCallId],
      refreshAfterResponse: [
        "restartDevServerTool",
        "createOrUpdateStoriesTool",
      ].includes(toolName),
    });
  };

  // Normalize content to combined format
  const combinedContent = normalizeToMaxFormat(message);

  const toolCallStatuses: { [toolCallId: string]: "succeeded" | "failed" } = {};
  // Start with external tool results if provided
  const toolCallResults: { [toolCallId: string]: any } = {
    ...(externalToolResults || {}),
  };

  combinedContent.forEach((block) => {
    if (block.type === "tool-result" && block.toolCallId) {
      try {
        const result =
          typeof block.content === "string"
            ? JSON.parse(block.content)
            : block.content;

        toolCallResults[block.toolCallId] = result;

        if (result && typeof result === "object") {
          if (result.status) {
            toolCallStatuses[block.toolCallId] =
              result.status === "success" ? "succeeded" : "failed";
          } else {
            // If we have a valid result object but no status, assume succeeded
            toolCallStatuses[block.toolCallId] = "succeeded";
          }
        } else {
          toolCallStatuses[block.toolCallId] = "succeeded";
        }
      } catch {
        toolCallStatuses[block.toolCallId] = "succeeded";
      }
    }
  });

  // Also check external tool results for statuses
  if (externalToolResults) {
    for (const [toolCallId, result] of Object.entries(externalToolResults)) {
      if (
        !toolCallStatuses[toolCallId] &&
        result &&
        typeof result === "object"
      ) {
        if (result.status) {
          toolCallStatuses[toolCallId] =
            result.status === "success" ? "succeeded" : "failed";
        } else {
          toolCallStatuses[toolCallId] = "succeeded";
        }
      }
    }
  }

  // Second pass: process all content blocks
  combinedContent.forEach((block) => {
    if (block.type === "reasoning") {
      segments.push({
        type: "reasoning",
        reasoning: block.reasoning,
      });
    } else if (block.type === "text") {
      // OLD: Text content (includes XML-based reasoning)
      processTextContent(block.text || "");
    } else if (block.type === "tool-call") {
      processToolCall(block, toolCallStatuses, toolCallResults);
    }
  });

  return segments;
};

export type AssistantInstruction =
  | {
      type: "fileChange";
      filepath: string;
      command: "create" | "str_replace" | "insert" | "overwrite";
      old_str?: string;
      new_str?: string;
      insert_line?: number;
      file_text?: string;
      replace_all?: boolean;
    }
  | {
      type: "newFile";
      filepath: string;
      content: string;
    }
  | {
      type: "newStoryboard";
      id: string;
      storyboardName: string;
      width?: number;
      height?: number;
      x?: number;
      y?: number;
      filepath?: string;
      contents: string;
    }
  | {
      type: "sqlMigration";
      filename: string;
    }
  | {
      type: "edgeFunctionDeployment";
      filename: string;
    }
  | {
      type: "terminalCommand";
      command: string;
    }
  | {
      type: "convexSync";
    }
  | {
      type: "reloadCodebase";
    }
  | {
      type: "restartDevServer";
    };

export type TracedAssistantInstruction = AssistantInstruction & {
  traceId?: string;
};

export type AssistantInstructionError =
  | {
      type: "blindEdit";
      filename: string;
    }
  | {
      type: "packageJsonChange";
      filename: string;
    }
  | {
      type: "textEditorError"; // filetofixwithinfo
      filename: string;
      error: string;
    }
  | {
      type: "brokenSegmentJson";
      segment: MessageSegment;
    }
  | {
      type: "incompleteSegmentJson";
      segment: MessageSegment;
      message: Message;
      filepath?: string;
    };

export const parseAssistantMessage = (
  message: Message,
  traceId?: string,
): {
  instructions: TracedAssistantInstruction[];
  errors: AssistantInstructionError[];
} => {
  const instructions: AssistantInstruction[] = [];
  const errors: AssistantInstructionError[] = [];
  let reloadCodebase = false;
  let restartDevServer = false;

  const segments = parseMessageContentBlocks(message);
  const aiChangeSegments = segments.filter((s) => s.type === "aiChanges");

  for (const changeSegment of aiChangeSegments) {
    if (changeSegment.jsonIsBroken) {
      errors.push({ type: "brokenSegmentJson", segment: changeSegment });
      continue;
    }

    const aiChanges: EngSpecFileDiff[] = (
      changeSegment.aiChanges?.changes || []
    ).filter(Boolean);

    for (const aiChange of aiChanges) {
      // Handle non-file changes first, these are valid even if the JSON is
      // incomplete since they (at least for now) only rely on a single field
      // being defined.
      const {
        runSqlMigration,
        deployEdgeFunction,
        termCommand,
        runConvexSync,
        requiresRestart,
      } = aiChange as EngSpecFileDiff;

      if (requiresRestart) {
        restartDevServer = true;
      }

      if (runSqlMigration) {
        instructions.push({ type: "sqlMigration", filename: runSqlMigration });
        continue;
      }
      if (deployEdgeFunction) {
        instructions.push({
          type: "edgeFunctionDeployment",
          filename: deployEdgeFunction,
        });
        continue;
      }
      if (termCommand) {
        instructions.push({ type: "terminalCommand", command: termCommand });
        reloadCodebase = true;
        continue;
      }
      if (runConvexSync) {
        instructions.push({ type: "convexSync" });
        reloadCodebase = true;
        continue;
      }

      // From here on out, we're dealing with file changes
      const { storyboardName, size, topLeftCorner, change } = aiChange;
      if (storyboardName) {
        if (!change?.newFullFileContent || changeSegment.jsonIncomplete) {
          // Only truly an error on our part if the JSON was supposed to be complete, otherwise its just a context limit hit
          if (!changeSegment.jsonIncomplete) {
            track("AI_CHAT_ERROR", {
              data: aiChange,
              error:
                "No change.newFullFileContent property for storyboard instruction provided",
            });
          }
          continue;
        }
        instructions.push({
          type: "newStoryboard",
          id: uuidv4().toString(),
          storyboardName,
          width: size?.width,
          height: size?.height,
          x: topLeftCorner?.x,
          y: topLeftCorner?.y,
          contents: change.newFullFileContent,
        });
        continue;
      }

      const { path: filepath } = aiChange;

      // Only allow complete JSONs to affect files, to avoid writing in empty values incorectly.
      const lastChange = aiChanges.indexOf(aiChange) === aiChanges.length - 1;
      const changeIsIncomplete = changeSegment.jsonIncomplete && lastChange;
      if (changeIsIncomplete) {
        errors.push({
          type: "incompleteSegmentJson",
          segment: changeSegment,
          filepath: aiChange.path,
          message,
        });
        continue;
      }

      if (!filepath) {
        track("AI_CHAT_ERROR", {
          data: aiChange,
          error: "No filepath property for instruction provided",
        });
        continue;
      }

      if (filepath.endsWith("package.json")) {
        errors.push({ type: "packageJsonChange", filename: filepath });
        continue;
      }

      if (aiChange.change?.newFullFileContent) {
        let contents = aiChange.change.newFullFileContent;
        if (typeof contents === "object") {
          contents = JSON.stringify(contents, null, 2);
        }
        instructions.push({
          type: "newFile",
          filepath: filepath,
          content: contents,
        });
        continue;
      }

      const { command } = aiChange;
      if (command) {
        if (!VALID_COMMANDS.has(command)) {
          const lowerCommand = command.toLowerCase();
          if (
            lowerCommand?.includes("read") &&
            lowerCommand?.includes("file")
          ) {
            errors.push({
              type: "textEditorError",
              filename: filepath,
              error:
                "Cannot read files inside ai-changes, use the tool call instead",
            });
          } else {
            errors.push({
              type: "textEditorError",
              filename: filepath,
              error: `Invalid command ${command || ""}`,
            });
          }
          continue;
        } else {
          instructions.push({
            type: "fileChange",
            filepath: filepath,
            command: command,
            old_str: aiChange.old_str,
            new_str: aiChange.new_str,
            insert_line: aiChange.insert_line,
            file_text: aiChange.file_text,
            replace_all: aiChange.replace_all,
          });
          continue;
        }
      }
    }
  }

  if (reloadCodebase) {
    instructions.push({ type: "reloadCodebase" });
  }
  if (restartDevServer) {
    instructions.push({ type: "restartDevServer" });
  }

  return { instructions: instructions.map((i) => ({ ...i, traceId })), errors };
};

export const useCreateMessageForChat = () => {
  const canvasId = useCanvasId();
  const selectedConversationId = useAiActionStore(
    (state) => state.selectedConversationId,
  );
  const { selectedTreeElements, selectedStoryboards } = useSelectedElements();
  const userId = useUserId();

  const createUserMessage = ({
    userPrompt,
    attachedFiles,
    chatOnlyMode,
  }: {
    userPrompt: string;
    attachedFiles?: UploadedFile[];
    chatOnlyMode?: boolean;
  }): Message => {
    const userMessageContext: MessageContext = {
      files: attachedFiles ?? [],
      totalUserSelection: [
        ...selectedTreeElements,
        ...selectedStoryboards.map((sb) => ({
          ...sb,
          isStoryboard: true,
        })),
      ],
    };

    return {
      id: uuidv4().toString(),
      conversation_id: selectedConversationId || "", // This should always be set if this function is being called
      canvas_id: canvasId,
      content: userPrompt,
      role: "user",
      message_context: userMessageContext,
      created_at: new Date().toISOString(),
      tool_call_id: null,
      tool_calls: null,
      chat_only_mode: !!chatOnlyMode,
      auth0_user_id_sent: userId || null,
    };
  };

  const createAssistantMessage = (message: Partial<Message> = {}): Message => {
    return {
      id: message.id || uuidv4().toString(),
      conversation_id: selectedConversationId || "",
      canvas_id: canvasId,
      content: message.content || "",
      role: message.role || "assistant",
      message_context: message.message_context,
      tool_calls: message.tool_calls || null,
      tool_call_id: message.tool_call_id || null,
      created_at: message.created_at || new Date().toISOString(),
      auth0_user_id_sent: message.role === "user" ? userId || null : null,
      chat_only_mode: message.chat_only_mode || false,
    };
  };

  return { createUserMessage, createAssistantMessage };
};

// Convert userMessage to Claude Code compliant format
export const toClaudeCodeUserMessage = (userMessage: any): any[] => {
  const contentBlocks: any[] = [
    {
      type: "text",
      text: userMessage.content,
    },
  ];

  // Add selection context as structured blocks if available
  if (userMessage.message_context) {
    const context = userMessage.message_context;

    // Add selected files/folders/storyboards
    if (context.totalUserSelection?.length) {
      contentBlocks.push({
        type: "text",
        text: `<tempo_selection_context>\n${JSON.stringify({
          selectedElements: context.totalUserSelection,
        })}\n</tempo_selection_context>`,
      });
    }

    // Add code snippet
    if (context.codeSnippet) {
      contentBlocks.push({
        type: "text",
        text: `<tempo_code_snippet>\n${JSON.stringify({
          codeSnippet: context.codeSnippet,
          diffCodeSnippet: context.diffCodeSnippet,
        })}\n</tempo_code_snippet>`,
      });
    }

    // Add attached files - handle images as proper ImageBlockParam and non-images as metadata
    if (context.files?.length) {
      const imageFiles: any[] = [];
      const nonImageFiles: any[] = [];

      // Separate images from other files
      context.files.forEach((file) => {
        if (file.type === "image") {
          imageFiles.push(file);
        } else {
          nonImageFiles.push(file);
        }
      });

      // Add images as proper Claude ImageBlockParam content blocks
      imageFiles.forEach((imageFile) => {
        try {
          // Determine media type from mimeType or file extension
          const getMediaType = (
            mimeType: string,
            fileName: string,
          ): "image/jpeg" | "image/png" | "image/gif" | "image/webp" => {
            // First try to use mimeType
            if (mimeType === "image/jpeg" || mimeType === "image/jpg")
              return "image/jpeg";
            if (mimeType === "image/png") return "image/png";
            if (mimeType === "image/gif") return "image/gif";
            if (mimeType === "image/webp") return "image/webp";

            // Fallback to file extension
            const ext = fileName.toLowerCase().split(".").pop();
            if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
            if (ext === "png") return "image/png";
            if (ext === "gif") return "image/gif";
            if (ext === "webp") return "image/webp";

            // Default to jpeg
            return "image/jpeg";
          };

          const mediaType = getMediaType(imageFile.mimeType, imageFile.name);

          if (imageFile.url.startsWith("data:")) {
            // Base64 encoded image
            const base64Data = imageFile.url.split(",")[1]; // Remove data:image/xxx;base64, prefix
            contentBlocks.push({
              type: "image",
              source: {
                type: "base64",
                media_type: mediaType,
                data: base64Data,
              },
            });
          } else {
            // URL-based image
            contentBlocks.push({
              type: "image",
              source: {
                type: "url",
                url: imageFile.url,
              },
            });
          }
        } catch (error) {
          console.warn(
            "Failed to process image file for Claude Code:",
            imageFile,
            error,
          );
          // Fallback: treat as non-image file
          nonImageFiles.push(imageFile);
        }
      });

      // Add non-image files as metadata (for reference)
      if (nonImageFiles.length > 0) {
        contentBlocks.push({
          type: "text",
          text: `<tempo_attached_files>\n${JSON.stringify({
            files: nonImageFiles,
          })}\n</tempo_attached_files>`,
        });
      }
    }
  }

  return contentBlocks;
};

// Convert Claude Code content blocks back to MessageContext and clean content for display
export const fromClaudeCodeUserMessage = (
  messageContent: any[],
): {
  context: Partial<MessageContext>;
  cleanedContent: any[];
} => {
  const context: Partial<MessageContext> = {};
  const cleanedContent: any[] = [];

  messageContent.forEach((block) => {
    if (block.type === "text" && block.text) {
      let cleanedText = block.text;

      // Extract tempo_selection_context and remove from display
      const selectionMatch = block.text.match(
        /<tempo_selection_context>\n(.+?)\n<\/tempo_selection_context>/s,
      );
      if (selectionMatch) {
        try {
          const parsed = JSON.parse(selectionMatch[1]);
          if (parsed.selectedElements) {
            context.totalUserSelection = parsed.selectedElements;
          }
          // Remove the context block from display text
          cleanedText = cleanedText.replace(
            /<tempo_selection_context>\n.+?\n<\/tempo_selection_context>/s,
            "",
          );
        } catch (e) {
          console.warn("Failed to parse tempo_selection_context:", e);
        }
      }

      // Extract tempo_code_snippet and remove from display
      const codeSnippetMatch = block.text.match(
        /<tempo_code_snippet>\n(.+?)\n<\/tempo_code_snippet>/s,
      );
      if (codeSnippetMatch) {
        try {
          const parsed = JSON.parse(codeSnippetMatch[1]);
          if (parsed.codeSnippet) {
            context.codeSnippet = parsed.codeSnippet;
          }
          if (parsed.diffCodeSnippet) {
            context.diffCodeSnippet = parsed.diffCodeSnippet;
          }
          // Remove the context block from display text
          cleanedText = cleanedText.replace(
            /<tempo_code_snippet>\n.+?\n<\/tempo_code_snippet>/s,
            "",
          );
        } catch (e) {
          console.warn("Failed to parse tempo_code_snippet:", e);
        }
      }

      // Extract tempo_attached_files and remove from display
      const attachedFilesMatch = block.text.match(
        /<tempo_attached_files>\n(.+?)\n<\/tempo_attached_files>/s,
      );
      if (attachedFilesMatch) {
        try {
          const parsed = JSON.parse(attachedFilesMatch[1]);
          if (parsed.files) {
            context.files = parsed.files;
          }
          // Remove the context block from display text
          cleanedText = cleanedText.replace(
            /<tempo_attached_files>\n.+?\n<\/tempo_attached_files>/s,
            "",
          );
        } catch (e) {
          console.warn("Failed to parse tempo_attached_files:", e);
        }
      }

      // Only add the block to cleaned content if there's remaining text after removing context
      cleanedText = cleanedText.trim();
      if (cleanedText) {
        cleanedContent.push({
          ...block,
          text: cleanedText,
        });
      }
    } else if (block.type === "image") {
      // Handle image blocks - convert them back to UploadedFile format for context.files
      try {
        const imageFile: any = {
          type: "image",
          name: "image", // Default name since Claude Code doesn't preserve original filename
          mimeType: "image/jpeg", // Default, could be inferred from URL or source
        };

        if (block.source?.type === "url") {
          imageFile.url = block.source.url;
          // Try to extract filename from URL
          const urlParts = block.source.url.split("/");
          const filename = urlParts[urlParts.length - 1];
          if (filename && filename.includes(".")) {
            imageFile.name = decodeURIComponent(filename);
            // Infer mimeType from extension
            const extension = filename.split(".").pop()?.toLowerCase();
            if (extension === "png") imageFile.mimeType = "image/png";
            else if (extension === "gif") imageFile.mimeType = "image/gif";
            else if (extension === "webp") imageFile.mimeType = "image/webp";
          }
        } else if (block.source?.type === "base64") {
          imageFile.url = `data:${block.source.media_type};base64,${block.source.data}`;
          imageFile.mimeType = block.source.media_type;
        }

        // Add to context.files if not already present
        if (!context.files) {
          context.files = [];
        }
        context.files.push(imageFile);

        // Also add the image block to cleaned content for display
        cleanedContent.push(block);
      } catch (e) {
        console.warn("Failed to process image block:", e);
        // Still add the block to cleaned content for display
        cleanedContent.push(block);
      }
    } else {
      // Other block types (tool_use, tool_result, etc.) pass through unchanged
      cleanedContent.push(block);
    }
  });

  return { context, cleanedContent };
};
